import { createSlice, isAnyOf } from "@reduxjs/toolkit";

import { RootState } from "@/redux";
import { resetAuth } from "@/pages/Public/redux/auth.slice";

import { searchDefaultsApi } from "./searchDefaults.api";
import { PersonLastSearch, PersonSearchDefaults } from "./searchDefaults.schema";

export interface SearchDefaultsState {
	/** The company preset; null until the first answer. */
	companyPreset: PersonSearchDefaults | null;
	/** The user's last search; null before their first search. */
	lastSearch: PersonLastSearch | null;
	/** English names of the country codes either search uses, by code. */
	countryNames: Record<string, string>;
	/** Whether an answer has arrived — a form waits for this before it prefills. */
	loaded: boolean;
}

const initialState: SearchDefaultsState = {
	companyPreset: null,
	lastSearch: null,
	countryNames: {},
	loaded: false,
};

const { endpoints } = searchDefaultsApi;

export const searchDefaultsSlice = createSlice({
	name: "searchDefaults",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		// The last search is one person's: it must not outlive their session in this tab.
		builder.addCase(resetAuth, () => initialState);
		builder.addMatcher(
			isAnyOf(
				endpoints.getSearchDefaults.matchFulfilled,
				endpoints.updateCompanySearchDefaults.matchFulfilled,
				endpoints.regenerateCompanySearchDefaults.matchFulfilled,
				endpoints.saveLastPersonSearch.matchFulfilled
			),
			(state, { payload }) => {
				state.companyPreset = payload.companyPreset;
				state.lastSearch = payload.lastSearch;
				state.countryNames = { ...state.countryNames, ...payload.countryNames };
				state.loaded = true;
			}
		);
	},
});

export const searchDefaultsReducer = searchDefaultsSlice.reducer;

/**
 * A plain read, not `createSelector`: the slice is already the shape the forms use, and a memoized
 * identity trips reselect's dev-mode check on every render. Both branches return a stable object.
 */
export const searchDefaultsSelector = (state: RootState): SearchDefaultsState =>
	(state[searchDefaultsSlice.name] as SearchDefaultsState | undefined) ?? initialState;
